// A searchable multi-select: the chosen values as removable chips, and a box that filters the rest as you
// type. Used for the contract vehicles and consortia a feed leaves out.
import { useId, useRef, useState } from "react";

export interface PickOption {
  value: string;
  label: string;
  sub?: string;
  count?: number;
  url?: string;
}

export function GroupPicker({
  options,
  chosen,
  onChange,
  placeholder,
  label,
  showCounts,
  removeTitle = "Remove",
}: {
  options: PickOption[];
  chosen: Set<string>;
  onChange: (next: Set<string>) => void;
  placeholder: string;
  label: string;
  showCounts: boolean;
  removeTitle?: string;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const q = text.trim().toLowerCase();
  const matches = options.filter((o) => !chosen.has(o.value) && (!q || `${o.label} ${o.sub ?? ""}`.toLowerCase().includes(q)));
  const pick = (value: string) => {
    onChange(new Set([...chosen, value]));
    setText("");
    setActive(0);
    setOpen(false);  // typing opens it again; left open, the full list would cover what follows
  };
  const drop = (value: string) => {
    const next = new Set(chosen);
    next.delete(value);
    onChange(next);
  };
  const byValue = new Map(options.map((o) => [o.value, o]));
  return (
    <div className="picker">
      {chosen.size ? (
        <div className="rules picker-chosen">
          {[...chosen].map((v) => {
            const o = byValue.get(v);
            return (
              <span key={v} className="picker-chip">
                <button type="button" title={removeTitle} onClick={() => drop(v)}>
                  {o?.label ?? v} ×
                </button>
                {o?.url ? (
                  <a href={o.url} target="_blank" rel="noreferrer" title={`Open ${o.label}`}>
                    ↗
                  </a>
                ) : null}
              </span>
            );
          })}
        </div>
      ) : null}
      <div className="picker-box">
        <input
          ref={input}
          type="text"
          value={text}
          placeholder={placeholder}
          aria-label={label}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open && matches.length > 0}
          aria-controls={`${id}-list`}
          aria-activedescendant={open && matches[active] ? `${id}-${active}` : undefined}
          onChange={(e) => {
            setText(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              setOpen(true);
              setActive((a) => Math.max(0, Math.min(matches.length - 1, a + (e.key === "ArrowDown" ? 1 : -1))));
            } else if (e.key === "Enter") {
              e.preventDefault();  // never submit the feed form from here
              const o = matches[active];
              if (open && o) pick(o.value);
            } else if (e.key === "Escape" && open) {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setOpen(false);
            } else if (e.key === "Backspace" && !text && chosen.size) {
              drop([...chosen].at(-1)!);
            }
          }}
        />
        <div className="suggest" id={`${id}-list`} role="listbox" hidden={!open || !matches.length}>
          {open
            ? matches.map((o, i) => (
                <div
                  key={o.value}
                  className="sg"
                  role="option"
                  id={`${id}-${i}`}
                  aria-selected={i === active}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(o.value);
                  }}
                >
                  <span className="sg-main">
                    <span className="sg-label">{o.label}</span>
                    {o.sub ? <span className="sg-sub">{o.sub}</span> : null}
                  </span>
                  {showCounts && o.count ? <span className="sg-hint">{o.count} here</span> : null}
                </div>
              ))
            : null}
        </div>
      </div>
    </div>
  );
}
